import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import type { Serie, SliceTooltipProps } from "@nivo/line"
import { ResponsiveLine } from "@nivo/line"
import { Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

import { getTheme } from "../../../hooks/UseTheme"
import { NivoTheme } from "../../../themes/nivoTheme"
import { FEATURE_NESTING_SEPARATOR } from "../../../utils/constants"
import { annotateNumber, customColorScheme } from "../../../utils/genericHelpers"
import { KonanEmptyState } from "../../utility/KonanEmptyState"
import type { ChartsContainerProps } from "../ChartsContainer"
import { ChartsContainer } from "../ChartsContainer"

interface LineGraphProps extends Omit<ChartsContainerProps, "children" | "emptyState"> {
  title: string
  subHeader?: React.ReactElement
  data: Serie[]
  isLoading: boolean
  graphHeight: number
  selectProps?: {
    options: string[]
    value: string
    setValue: (val: string) => void
    multiple?: boolean
  }
  variant?: "linear" | "time"
  emptyState?: { title: string; subTitle?: string }
  tooltipComponent?: (slice: SliceTooltipProps) => React.ReactElement
  timeScalePrecision?: "hour" | "day" | "month" | "year"
}

/**
 * Formats a date string based on the specified precision level
 * @param date - The date string to format (expected format: YYYY-MM-DD HH:mm:ss.SSS)
 * @param precision - The desired precision level for the formatted output
 *                   - 'hour': Returns date in format "Do MMM hh:mm" (e.g., "1st Jan 03:30")
 *                   - 'day': Returns date in format "DD/MM" (e.g., "01/01")
 *                   - 'month'/'year': Returns date in format "DD/MM/YYYY" (e.g., "01/01/2024")
 * @returns A formatted date string according to the specified precision
 */
const formatBasedOnPrecision = (date: string, precision?: "hour" | "day" | "month" | "year"): string => {
  switch (precision) {
    case "hour":
      return moment(date).format("Do MMM hh:mm")

    case "day":
      return moment(date).format("DD/MM")

    default:
      return moment(date).format("DD/MM/YYYY")
  }
}

// TODO:: create 2 components one for dates and another for linear values
export function LineGraph(props: Readonly<LineGraphProps>): React.ReactElement {
  const { t } = useTranslation("graphs")

  const {
    data,
    title,
    graphHeight,
    isLoading,
    subHeader,
    selectProps,
    emptyState,
    customHeaderComponent,
    variant = "linear",
    tooltipComponent,
    tooltipText,
    timeScalePrecision,
  } = props

  const theme = getTheme()

  return (
    <ChartsContainer
      title={title}
      isLoading={isLoading}
      graphHeight={graphHeight + 80}
      selectProps={!emptyState ? selectProps : undefined}
      customHeaderComponent={customHeaderComponent}
      tooltipText={tooltipText}
    >
      {emptyState ? (
        <Grid container item xs={12} justifyContent={"center"} alignItems={"center"}>
          <KonanEmptyState
            title={emptyState?.title ?? `${title} ${t("lineGraph.emptyState.unavailable")}`}
            subTitle={emptyState?.subTitle}
          />
        </Grid>
      ) : (
        <Grid container gap={1} py={2}>
          <Grid item xs={12}>
            {subHeader}
          </Grid>
          <Grid item xs={12} style={{ height: graphHeight, padding: "8px 0px 0px" }}>
            <ResponsiveLine
              data={data}
              theme={NivoTheme(theme)}
              margin={{ top: 20, right: 30, bottom: 30, left: 50 }}
              colors={customColorScheme()}
              xScale={{
                type: variant,
                ...(variant === "time" && {
                  format: "%Y-%m-%d %H:%M:%S.%L",
                  useUTC: false,
                  precision: timeScalePrecision,
                }),
              }}
              yScale={{
                type: "linear",
                min: "auto",
                max: "auto",
                stacked: false,
                reverse: false,
              }}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                truncateTickAt: 0,
                format: (tick) =>
                  variant === "linear" ? annotateNumber(tick, 1) : formatBasedOnPrecision(tick, timeScalePrecision),
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: t("lineGraph.axis.count"),
                legendOffset: -40,
                legendPosition: "middle",
                truncateTickAt: 0,
                format: (tick) => annotateNumber(tick, 1),
              }}
              lineWidth={2}
              enablePoints={false}
              enableCrosshair={false}
              useMesh={true}
              legends={[]}
              enableSlices="x"
              sliceTooltip={(props) =>
                tooltipComponent?.(props) ?? (
                  <div className={"chart-tooltip"} style={{ width: "fit-content" }}>
                    {props.slice.points.map((entry) => (
                      <Typography variant="p" variantColor={2} key={entry.color}>
                        {entry.serieId.toString().replaceAll(FEATURE_NESTING_SEPARATOR, ".")}:{" "}
                        <strong>
                          {variant === "time"
                            ? moment(entry.data.x).format("DD MM YYYY").toString()
                            : (entry.data.x as string)}
                        </strong>
                        ,<strong>{entry.data.y as string}</strong>
                      </Typography>
                    ))}
                  </div>
                )
              }
            />
          </Grid>
        </Grid>
      )}
    </ChartsContainer>
  )
}
